import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Monitor, Tablet, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { Header } from "./Schools";

const SI = "px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 bg-white";
const POSITIONS = [
  { value: "left-bottom", label: "Left Bottom" },
  { value: "left-center", label: "Left Center" },
  { value: "left-top", label: "Left Top" },
  { value: "right-bottom", label: "Right Bottom" },
  { value: "right-center", label: "Right Center" },
  { value: "right-top", label: "Right Top" },
];
const DISPLAY_MODES = [
  { value: "live", label: "Live Only" },
  { value: "total", label: "Total Only" },
  { value: "both", label: "Both (click to toggle)" },
];
const ICONS = [
  { value: "users", label: "Users" },
  { value: "eye", label: "Eye" },
];

function Lbl({ children }) { return <label className="block text-xs font-semibold text-gray-700 mb-1">{children}</label>; }
function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60"><span className="text-sm font-semibold text-gray-700">{title}</span></div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}
function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer py-1 select-none">
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${checked ? "bg-indigo-600" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </button>
      <span className="text-sm font-medium text-gray-800">{label}</span>
    </label>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }
function ColorRow({ label, value, onChange }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <div className="flex gap-2">
        <input type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded border border-gray-200 cursor-pointer shrink-0" />
        <input className={SI} value={value} onChange={e => onChange(e.target.value)} />
      </div>
    </div>
  );
}

export default function VisitorCounterWidget() {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/visitor-counter/settings");
      setSettings(data);
    } catch { toast.error("Failed to load visitor counter settings"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/visitor-counter/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  const TABS = [
    { id: "general", label: "General" },
    { id: "style", label: "Style & Position" },
    { id: "visibility", label: "Visibility" },
  ];

  if (loading || !settings) {
    return (
      <div className="fade-up">
        <Header title="Visitor Counter" subtitle="Configure the floating visitor counter widget." />
        <div className="mt-6 space-y-4 max-w-3xl">{[1, 2, 3].map(n => <div key={n} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />)}</div>
      </div>
    );
  }

  return (
    <div className="fade-up pb-12">
      <Header
        title="Visitor Counter"
        subtitle="A floating badge showing live and/or total visitor counts, connected to your visitor analytics."
        actions={
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60">
            <Save className="w-4 h-4" />{saving ? "Saving…" : "Save Settings"}
          </button>
        }
      />

      <div className="flex gap-0 mt-6 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 max-w-3xl space-y-5">

        {tab === "general" && (<>
          <Card title="Enable / Disable">
            <Toggle label="Show Floating Visitor Counter Widget" checked={!!settings.enabled} onChange={v => set("enabled", v)} />
            <p className="text-xs text-gray-400">
              Counts are read from the same visitor tracking used by Visitor Analytics — no separate tracking settings here.
            </p>
          </Card>

          <Card title="Display Mode">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {DISPLAY_MODES.map(m => (
                <button key={m.value} type="button" onClick={() => set("display_mode", m.value)}
                  className={`text-xs px-3 py-2.5 rounded-lg border transition ${settings.display_mode === m.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400">
              "Both" shows the live count first; clicking the widget toggles to the total count and back. It always stays on the current page — it never navigates anywhere.
            </p>
          </Card>

          <Card title="Labels">
            <Row2>
              <div><Lbl>Live Count Label</Lbl><input className={SI} value={settings.label_live} onChange={e => set("label_live", e.target.value)} placeholder="online now" /></div>
              <div><Lbl>Total Count Label</Lbl><input className={SI} value={settings.label_total} onChange={e => set("label_total", e.target.value)} placeholder="total visits" /></div>
            </Row2>
          </Card>

          <Card title="Icon">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ICONS.map(i => (
                <button key={i.value} type="button" onClick={() => set("icon", i.value)}
                  className={`text-xs px-3 py-2.5 rounded-lg border transition ${settings.icon === i.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {i.label}
                </button>
              ))}
            </div>
          </Card>
        </>)}

        {tab === "style" && (<>
          <Card title="Colors">
            <Row2>
              <ColorRow label="Background" value={settings.background_color} onChange={v => set("background_color", v)} />
              <ColorRow label="Text Color" value={settings.text_color} onChange={v => set("text_color", v)} />
            </Row2>
          </Card>

          <Card title="Shape & Shadow">
            <Row2>
              <div><Lbl>Border Radius (px)</Lbl><input type="number" className={SI} value={settings.border_radius} onChange={e => set("border_radius", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Size (px)</Lbl><input type="number" className={SI} value={settings.size} onChange={e => set("size", parseInt(e.target.value) || 56)} /></div>
              <div>
                <Lbl>Shadow</Lbl>
                <select className={SI} value={settings.shadow ? "on" : "off"} onChange={e => set("shadow", e.target.value === "on")}>
                  <option value="on">On</option><option value="off">Off</option>
                </select>
              </div>
            </Row2>
          </Card>

          <Card title="Position">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {POSITIONS.map(p => (
                <button key={p.value} type="button" onClick={() => set("position", p.value)}
                  className={`text-xs px-3 py-2.5 rounded-lg border transition ${settings.position === p.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400">Only the offsets relevant to the chosen position apply, but all four are saved so switching position later won't lose your values.</p>
            <Row2>
              <div><Lbl>Top Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_top} onChange={e => set("distance_top", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Bottom Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_bottom} onChange={e => set("distance_bottom", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Left Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_left} onChange={e => set("distance_left", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Right Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_right} onChange={e => set("distance_right", parseInt(e.target.value) || 0)} /></div>
            </Row2>
          </Card>

          <Card title="Stacking">
            <div><Lbl>Z-index</Lbl><input type="number" className={SI} value={settings.z_index} onChange={e => set("z_index", parseInt(e.target.value) || 55)} /></div>
            <p className="text-xs text-gray-400">Controls stacking order against other page elements. Higher values sit on top.</p>
          </Card>
        </>)}

        {tab === "visibility" && (
          <Card title="Device Visibility">
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "desktop_visible", label: "Desktop", Icon: Monitor },
                { key: "tablet_visible", label: "Tablet", Icon: Tablet },
                { key: "mobile_visible", label: "Mobile", Icon: Smartphone },
              ].map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => set(key, !settings[key])}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition ${settings[key] ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-400"}`}>
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-semibold">{label}</span>
                  <span className="text-[10px]">{settings[key] ? "Visible" : "Hidden"}</span>
                </button>
              ))}
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
